import { Clock, ExternalLink, LogIn, RefreshCw } from 'lucide-react';
import type { JSX } from 'react';

import { CtLogo } from '@/components/brand/index.js';
import { Button } from '@/components/ui/button.js';
import type { RuntimeMaintenance } from '@/lib/runtime-config.js';
import { safeHttpUrl } from '@/lib/safe-url.js';

/**
 * MaintenanceSplash — the downtime screen shown BEFORE login (and in the Teams
 * Tab), driven by the boot-time runtime config rather than the in-app query. It
 * stays free of router/query hooks so it can render above the app providers.
 *
 *   - brand mark + calm headline + supportive copy (with sensible defaults)
 *   - optional ETA pill and a "follow updates" link to the Teams channel
 *   - "Try again" re-fetches /v1/config in place (no full reload)
 *   - "Sign in" lets an admin proceed to the normal login; the post-login gate
 *     (AppShell) still enforces the allow-admins rule.
 */
export function MaintenanceSplash({
  maintenance,
  retrying,
  onRetry,
  onSignIn,
}: {
  maintenance: RuntimeMaintenance;
  retrying: boolean;
  onRetry: () => void;
  onSignIn: () => void;
}): JSX.Element {
  const title = maintenance.title || 'We’ll be right back';
  const message =
    maintenance.message ||
    'Agent is briefly down for maintenance. Thanks for your patience.';
  const channelUrl = safeHttpUrl(maintenance.channelUrl);

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center overflow-hidden bg-background px-6">
      <div className="flex w-full max-w-md flex-col items-center gap-8 text-center">
        <div className="flex items-center gap-3.5">
          <CtLogo
            variant="mark"
            tone="mono"
            className="size-11 text-[#18399b] [[data-theme='dark']_&]:text-white"
            aria-label="Customertimes"
          />
          <h1 className="text-2xl font-bold leading-[1.1] tracking-[-0.01em] text-foreground">
            Agent
          </h1>
        </div>

        <div className="flex flex-col items-center gap-3">
          <h2 className="text-3xl font-extrabold tracking-tight text-foreground">
            {title}
          </h2>
          <p className="text-base leading-relaxed text-muted-foreground">
            {message}
          </p>
        </div>

        {maintenance.eta ? (
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-muted/30 px-4 py-1.5 text-sm font-medium text-foreground">
            <Clock className="size-3.5 text-muted-foreground" aria-hidden />
            <span>{maintenance.eta}</span>
          </div>
        ) : null}

        {channelUrl ? (
          <a
            href={channelUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm font-medium text-[#18399b] hover:underline [[data-theme='dark']_&]:text-white"
          >
            Follow updates in our Teams channel
            <ExternalLink className="size-3.5" aria-hidden />
          </a>
        ) : null}

        <div className="flex flex-wrap items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onRetry}
            disabled={retrying}
            className="gap-2"
          >
            <RefreshCw
              className={retrying ? 'size-4 animate-spin' : 'size-4'}
              aria-hidden
            />
            {retrying ? 'Checking…' : 'Try again'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onSignIn}
            className="gap-2"
          >
            <LogIn className="size-4" aria-hidden />
            Sign in
          </Button>
        </div>
      </div>
    </div>
  );
}
